import { useEffect, useState } from 'react';
import { FileText, Sparkles, ClipboardCheck, Inbox, Loader2, Download } from 'lucide-react';
import { watchJobs, type StoredJob } from '../services/jobsService';
import { useAuth } from '../contexts/AuthContext';
import { markdownToWordBlob, titleToFilename } from '../utils/docx';

export default function History() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState<StoredJob[] | null>(null);

  useEffect(() => {
    if (authLoading || !user) return;
    const unsub = watchJobs((jobs) => setItems(jobs));
    return () => unsub();
  }, [user, authLoading]);

  const loading = authLoading || items === null;

  return (
    <div className="container-wide py-10">
      <h1 className="text-3xl font-display font-bold">History</h1>
      <p className="text-ink-600 mt-1">Your recent grading and generation runs.</p>

      {loading ? (
        <div className="mt-8 card p-14 text-center">
          <Loader2 className="w-8 h-8 text-brand-600 animate-spin mx-auto" />
          <p className="mt-3 text-ink-500 text-sm">Loading your history…</p>
        </div>
      ) : items.length === 0 ? (
        <div className="mt-8 card p-14 text-center">
          <Inbox className="w-10 h-10 mx-auto text-ink-300" />
          <p className="mt-3 text-ink-500">Nothing here yet — run an evaluation or generate a report.</p>
        </div>
      ) : (
        <div className="mt-6 card divide-y divide-ink-100">
          {items.map((it) => (
            <Row key={it.id} item={it} />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ item }: { item: StoredJob }) {
  const { icon, bg, primaryText, secondaryText } = presentRow(item);

  return (
    <div className="p-4 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-xl grid place-items-center ${bg}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{primaryText}</div>
        <div className="text-xs text-ink-500">{secondaryText}</div>
      </div>
      {item.kind === 'generation' && <GenerationDownloads item={item} />}
      {typeof item.score === 'number' && item.grade && (
        <div className="text-right">
          <div className="font-display font-bold text-brand-700 text-xl leading-none">{item.score}</div>
          <div className="text-xs text-emerald-600">{item.grade}</div>
        </div>
      )}
    </div>
  );
}

/**
 * Per-row downloads for generation jobs. The full markdown is retained for
 * 24 h via Firestore TTL (see jobsService.GENERATION_RETENTION_MS). After that
 * window the `markdown` field is gone, so we show an "expired" hint instead of
 * broken buttons.
 */
function GenerationDownloads({ item }: { item: StoredJob }) {
  const expired = item.expiresAt ? new Date(item.expiresAt).getTime() <= Date.now() : false;
  const hasMarkdown = !!item.markdown;

  if (!hasMarkdown || expired) {
    return <div className="text-xs text-ink-400">Expired</div>;
  }

  const title = item.title ?? 'report';
  const md = item.markdown as string;

  const download = (kind: 'md' | 'txt' | 'doc') => {
    const blob =
      kind === 'doc'
        ? markdownToWordBlob(md, title)
        : new Blob([md], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${titleToFilename(title)}.${kind}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex gap-2 flex-wrap">
      <button onClick={() => download('md')} className="btn-secondary btn-sm" title="Download Markdown">
        <Download className="w-4 h-4" /> .md
      </button>
      <button onClick={() => download('txt')} className="btn-secondary btn-sm" title="Download text">
        <Download className="w-4 h-4" /> .txt
      </button>
      <button onClick={() => download('doc')} className="btn-primary btn-sm" title="Download as Word">
        <Download className="w-4 h-4" /> Word
      </button>
    </div>
  );
}

function presentRow(it: StoredJob) {
  const date = new Date(it.createdAt).toLocaleString();
  if (it.kind === 'evaluation') {
    return {
      icon: <FileText className="w-5 h-5" />,
      bg: 'bg-brand-50 text-brand-700',
      primaryText: it.fileName ?? 'Untitled',
      secondaryText: `Evaluation · ${date}`,
    };
  }
  if (it.kind === 'rubric') {
    return {
      icon: <ClipboardCheck className="w-5 h-5" />,
      bg: 'bg-emerald-50 text-emerald-700',
      primaryText: it.fileName ?? 'Untitled',
      secondaryText: `Rubric: ${it.rubricName ?? '—'} · ${date}`,
    };
  }
  // Generation rows: if we still have the markdown stashed for re-download,
  // hint at the 24 h retention window so users aren't surprised when .md/.txt/
  // Word buttons disappear tomorrow.
  const retention = retentionHint(it);
  return {
    icon: <Sparkles className="w-5 h-5" />,
    bg: 'bg-fuchsia-50 text-fuchsia-700',
    primaryText: it.title ?? 'Untitled',
    secondaryText: `Generation · ${it.words ?? 0} words · ${date}${retention ? ` · ${retention}` : ''}`,
  };
}

/**
 * Human-friendly "expires in …" hint, or "" once the markdown is gone.
 *
 * Unit is picked by magnitude so 30-day Pro/Team windows don't render as
 * "720h 0m": days when ≥ 2 d left, hours when ≥ 1 h, minutes otherwise.
 */
function retentionHint(it: StoredJob): string {
  if (!it.markdown || !it.expiresAt) return '';
  const msLeft = new Date(it.expiresAt).getTime() - Date.now();
  if (msLeft <= 0) return 'download expired';
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (msLeft >= 2 * day) {
    const days = Math.floor(msLeft / day);
    return `download expires in ${days} day${days === 1 ? '' : 's'}`;
  }
  if (msLeft >= hour) {
    const hours = Math.floor(msLeft / hour);
    const minutes = Math.floor((msLeft % hour) / minute);
    return `download expires in ${hours}h ${minutes}m`;
  }
  const minutes = Math.max(1, Math.floor(msLeft / minute));
  return `download expires in ${minutes}m`;
}
